export interface NationalFramework {
  BE: string;
  FI: string;
  EL: string;
  ES: string;
  FR: string;
}

export interface ComplianceItem {
  id: string;
  title: string;
  iso27001: string;
  nist: string;
  etsi: string;
  cen: string;
  national: NationalFramework;
}

export type ComplianceChecklist = { [key: string]: string };
export type ExpandedItems = { [key: string]: boolean };

export interface CategoryStat {
  total: number;
  completed: number;
  percentage: number;
}
export type CategoryStats = { [key: string]: CategoryStat };

export const CATEGORIES: { [key: string]: string[] } = {
  'Governance': ['1.1', '1.2'],
  'Risk Management': ['2.1', '2.2', '2.3', '7.1'],
  'Incident Management': ['3.1', '3.2', '3.3', '3.4', '3.5', '3.6'],
  'Business Continuity': ['4.1', '4.2', '4.3'],
  'Supply Chain': ['5.1', '5.2'],
  'Systems & Network': ['6.1', '6.2', '6.3', '6.4', '6.5', '6.6', '6.7', '6.8', '6.9', '6.10'],
  'Awareness & Training': ['8.1', '8.2'],
  'Cryptography': ['9.1'],
  'Human Resources': ['10.1', '10.2', '10.3', '10.4'],
  'Access Control': ['11.1', '11.2', '11.3', '11.4', '11.5', '11.6', '11.7'],
  'Asset Management': ['12.1', '12.2', '12.3', '12.4', '12.5'],
  'Physical Security': ['13.1', '13.2', '13.3'],
};

export const MAPPING_DATA: ComplianceItem[] = [
  {
    id: '1.1',
    title: 'Policy on the security of network and information systems',
    iso27001: '5.2, A.5.1, A.5.36, A.5.4, 9.3',
    nist: 'PR.AT-02, GV.PO-01, GV.PO-02, GV.OC-03, GV.RM-03, GV.OC-02, ID.IM-01, ID.IM-02, ID.IM-03, ID.IM-04',
    etsi: 'REQ 6.1-02, REQ 6.1-06, REQ 6.1-07, REQ 6.1-08, Clause 6.3',
    cen: 'ISP-01, ISP-02, OPS-01, OPS-02, OPS-03',
    national: {
      BE: 'BASIC: ID.GV-1.1, IMPORTANT: ID.GV-1.2, PR.IP-5.1, PR.IP-6.1, PR.PT2.1, PR.AT-4.1, ESSENTIAL: PR.PT-3.3, PR.PT-4.3',
      FI: 'WORKFORCE-3, PROGRAM-1, PROGRAM-2, Management activities, CRITICAL2, ARCHITECTURE-1',
      EL: 'Articles 6a, 6b, 6c',
      ES: 'Article 5, Article 6, Article 10, Article 12, Article 27, Annex II: [org.1] Security policy',
      FR: '2.B.1-IE/EE, 2.B.2-IE/EE, 2.B.3-IE/EE, 2.B.4-IE/EE, 2.B.5-IE/EE',
    },
  },
  {
    id: '1.2',
    title: 'Roles, responsibilities and authorities',
    iso27001: '5.3, A.5.2, A.5.3, A.5.4',
    nist: 'GV.RR-02, GV.SC-02, PR.AT-02, ID.IM-01, ID.IM-02, ID.IM-03, ID.IM-04',
    etsi: 'REQ-7.1.2-01, REQ-7.2-01X, REQ-7.2-03X, REQ 7.2-07X',
    cen: 'ISP-02, OIS-02',
    national: {
      BE: 'BASIC: RS.RP-1.1, IMPORTANT: ID.AM-6.1, PR.AT-2.1, PR.AT-4.1',
      FI: 'PROGRAM-1, PROGRAM-2, CRITICAL-2, WORKFORCE-2, WORKFORCE-3',
      EL: 'Articles 7a, 7b, 7c',
      ES: 'Article 11, Article 13, Annex II: [org.1] Security policy',
      FR: '2.A.1-IE/EE, 2.A.2-EE, 2.A.3-IE/EE',
    },
  },
  {
    id: '2.1',
    title: 'Risk management framework',
    iso27001: '6.1, 6.1.2, 6.1.3, 6.2, 8.2, 8.3, A5.7, Α.5.19, Α.5.20, Α.5.21',
    nist: 'ID.RA-01, ID.RA-02, ID.RA-03, ID.RA-04, ID.RA-05, ID.RA-06, GV.RM-03',
    etsi: 'Clause 5, Clause 6.3',
    cen: 'OIS-01, RM-01, RM-02, RM-03',
    national: {
      BE: 'BASIC: ID.GV-4.1, ID.RA5.1, IMPORTANT: ID.BE-4.1, ID.GV-4.2',
      FI: 'CRITICAL-2, RISK-1, RISK-2, RISK-3, RISK4, RISK-5',
      EL: 'Articles 5.1a, 5.1b, 5.1c, 5.1d, 5.2',
      ES: 'Article 7, Article 14, Annex II: [op.pl.1] Risk analysis',
      FR: '3.A.IE/EE, 16.1-EE, 16.2-EE, 16.3-EE',
    },
  },
  {
    id: '2.2',
    title: 'Compliance monitoring',
    iso27001: '9.2, A.5.31, A.5.35, A.5.36',
    nist: 'GV.OV-02, ID.IM-01, ID.IM-02, ID.IM-03, ID.IM-04',
    etsi: 'Clause 7.13, REQ-6.3-06X, REQ-6.3-07X, REQ-6.1-08',
    cen: 'CO-01, DOC-03, INQ-01, INQ-02, INQ-03',
    national: {
      BE: 'BASIC: RS.IM-1.1, IMPORTANT: ID.GV-1.2, ID.GV-3.2, ID.SC-4.1',
      FI: 'PROGRAM-1, PROGRAM-2',
      EL: 'Articles 9a, 9b, 9c',
      ES: 'Article 10, Article 28, Article 31, Article 32',
      FR: '2.A.1-IE/EE, 2.B.2-IE/EE, 2.B.4-IE/EE',
    },
  },
  {
    id: '2.3',
    title: 'Independent review of information and network security',
    iso27001: '9.2, 10.1, A.5.35, A.8.34',
    nist: 'GV.OV-02, ID.IM-01',
    etsi: 'Clause 7.13, REQ-7.2-11X, REQ-7.2-14X (d)',
    cen: 'CO-01, CO-02, CO-03, CO04',
    national: {
      BE: 'ESSENTIAL: ID.SC-4.2, PR.IP-7.2, DE.DP-5.2, DE.CM-2.2',
      FI: 'PROGRAM-2',
      EL: 'Articles 8a, 8b, 8c, 8d',
      ES: 'Article 31, ANNEX III - Security audit',
      FR: '3.B.2-EI/EE, 17.2-EE',
    },
  },
  {
    id: '3.1',
    title: 'Incident handling policy',
    iso27001: 'A.5.24',
    nist: 'GV.SC-08, RS.MA-01, RS.MA-05, RS.MI-01, RS.MI-02, ID.IM-01, ID.IM-04',
    etsi: 'REQ-7.9.2-12X, REQ-7.9.2-01X, REQ-7.9.2-04X',
    cen: 'ISP-02, IM-01, IM-07',
    national: {
      BE: 'BASIC: RS.RP-1.1, IMPORTANT: ID.AM-6.1, PR.IP-9.1',
      FI: 'RESPONSE-1, RESPONSE-2, RESPONSE-3, RESPONSE-5, CRITICAL-3',
      EL: 'Articles 24a, 24f',
      ES: 'Article 12, Article 24, Article 25, Article 33, Article 34',
      FR: '2.C.1-IE/EE, 2.C.2-IE/EE, 2.C.3-IE/EE',
    },
  },
  {
    id: '3.2',
    title: 'Monitoring and logging',
    iso27001: 'A.5.28, A.8.15, A.8.16, A.8.17',
    nist: 'RS.AN-06, RS.AN-07, ID.IM-01, ID.IM-02, ID.IM-03, ID.IM-04',
    etsi: 'REQ-7.9.1-01X, REQ-7.9.1-02X, REQ-7.9.1-03X, REQ-7.9.1-04X',
    cen: 'OPS-10, OPS-11, OPS-12, OPS-13, OPS-14, OPS-15',
    national: {
      BE: 'BASIC: PR.PT-1.1, DE.AE3.1, IMPORTANT: PR.AC-2.2, PR.AC-4.5',
      FI: 'SITUATION-1, SITUATION-2, SITUATION-3, ASSET-4',
      EL: 'Articles 24c, 24d, 24e',
      ES: 'Article 10, Article 21, Annex II: [op.mon.1] Intrusion detection',
      FR: '5.B.1-EE, 12.6-IE/EE, 12.7-EE, 13.1-IE/EE',
    },
  },
  {
    id: '3.3',
    title: 'Event reporting',
    iso27001: 'A.6.8',
    nist: 'RS.MI-01, RS.CO-02',
    etsi: 'REQ-7.9.2-12X, REQ-7.9.3-01X, REQ-7.9.3-02X',
    cen: 'IM-03, IM-04',
    national: {
      BE: 'BASIC: PR.AT-1.1, DE.CM3.1, IMPORTANT: PR.AT-1.2, DE.AE-3.2',
      FI: 'RESPONSE-1, WORKFORCE-2',
      EL: 'Article 21a.g',
      ES: 'Article 32, Article 33, Annex II: [op.exp.7] Incident management',
      FR: '4.2-IE/EE, 4.5-IE/EE, 6.2-EE, 12.2-EE',
    },
  },
  {
    id: '3.4',
    title: 'Event assessment and classification',
    iso27001: 'A.5.25',
    nist: 'DE.AE-04, RS.MA-02, RS.MA-03, RS.MA-04, ID.IM-01, ID.IM-02',
    etsi: 'REQ-7.9.3-01X, REQ-7.9.2-11X, REQ-7.9.4-01X, REQ-7.9.4-02X',
    cen: 'IM-02',
    national: {
      BE: 'BASIC: RS.IM-1.1, IMPORTANT: PR.IP-9.1, DE.AE-1.2, DE.AE-3.2',
      FI: 'RESPONSE-1, RESPONSE-2',
      EL: 'Article 24e',
      ES: 'Article 32, Article 33, Technical Security Instruction',
      FR: '3.A.1-IE/EE, 12.2-EE, 12.4-EE, 12.5-EE',
    },
  },
  {
    id: '3.5',
    title: 'Incident response',
    iso27001: 'A.5.26',
    nist: 'RS.MA-01, RS.MA-02, RS.MA-03, RS.MA-04, RS.CO-02, RS.CO-03',
    etsi: 'REQ-7.9.2-01X, REQ-7.9-09, REQ-7.9-12, Clause 7.9.2',
    cen: 'OIS-03, IM-01, IM-05, IM07, INQ-02, INQ-03',
    national: {
      BE: 'BASIC: RS.RP-1.1, IMPORTANT: PR.IP-9.1, ESSENTIAL: PR.IP-9.2',
      FI: 'RESPONSE-2, RESPONSE-3',
      EL: 'Articles 24a, 24b',
      ES: 'Article 32, Article 33, Annex II: [op.exp.7] Incident management',
      FR: '12.1-EE, 12.6-IE/EE, 13.6-EE, 14.1-IE/EE',
    },
  },
  {
    id: '3.6',
    title: 'Post-incident reviews',
    iso27001: 'A.5.27',
    nist: 'ID.IM-01, ID.IM-04, RS.AN-08',
    etsi: 'REQ-7.9.5-01X, REQ-7.9.5-03X, REQ-7.9.5-04X',
    cen: 'IM-06',
    national: {
      BE: 'BASIC: RS.IM-1.1, IMPORTANT: PR.P-7.1, RS.IM-1.2, RS.IM-2.1',
      FI: 'RESPONSE-3, RESPONSE-5',
      EL: 'Article 24f',
      ES: 'Article 32, Article 33, Annex II: [op.exp.7] Incident management',
      FR: '12.1-EE, 12.5-EE, 14.1-IE/EE, 14.4-EE',
    },
  },
  {
    id: '4.1',
    title: 'Business continuity and disaster recovery plan',
    iso27001: 'A5.29, A.5.30',
    nist: 'ID.IM-02, ID.IM-03, ID.IM-04, GV.OC-04, GV.SC-08, RC.RP-01, RC.RP-02',
    etsi: 'Clause 7.11',
    cen: 'BC-01, BC-02, BC-03, BC04',
    national: {
      BE: 'BASIC: ID.BE-5.1, PR.IP4.1, IMPORTANT: ID.SC-5.1, PR.IP-9.1',
      FI: 'RESPONSE-4, RESPONSE-5, CRITICAL-3',
      EL: 'Articles 25.1a, 25.1b, 25.1c',
      ES: 'Article 26, Article 27, Annex II: [op.cont.1] Impact analysis',
      FR: '12.4-EE, 13.1-IE/EE, 13.2-IE/EE',
    },
  },
  {
    id: '4.2',
    title: 'Backup management',
    iso27001: 'A.8.13, A.8.14',
    nist: 'PR.DS-11, RC.RP-01, RC.RP-02, ID.IM-03',
    etsi: 'Clause 7.11.2',
    cen: 'OPS-06, OPS-07, OPS-08, OPS-09',
    national: {
      BE: 'BASIC: PR.IP-4.1, RC.RP1.1, IMPORTANT: PR.IP-4.2, PR.DS-3.3',
      FI: 'RESPONSE-4, ASSET1, ASSET-2, CRITICAL-2, ARCHITECTURE-1',
      EL: 'Articles 25.1d, 25.1e, 25.1f, 25.1g',
      ES: 'Annex II: [op.cont.1] Impact analysis, [mp.info.6] Backups',
      FR: '12.6-IE/EE, 12.7-EE, 13.1-IE/EE, 13.2-IE/EE',
    },
  },
  {
    id: '4.3',
    title: 'Crisis management',
    iso27001: 'A.5.26, A.5.29, A.5.30',
    nist: 'RS.CO-02, RS.CO-03, PR.IR-03, DE.CM-01, ID.AM-03',
    etsi: 'Clause 7.11.3',
    cen: 'BC-03, OIS-03',
    national: {
      BE: 'BASIC: RS.CO-3.1, IMPORTANT: PR.IP-8.1, DE.DP-4.1',
      FI: 'RESPONSE-3, THREAT-2, CRITICAL-1, CRITICAL-3',
      EL: 'Articles 25.2a, 25.2b',
      ES: 'Article 26, Article 27, Annex II: [op.cont.1] Impact analysis',
      FR: '12.4-EE, 12.6-IE/EE, 12.7-EE, 13.1-IE/EE',
    },
  },
  {
    id: '5.1',
    title: 'Supply chain security policy',
    iso27001: 'A.5.19, A.5.20, A.5.21, A.8.30',
    nist: 'GV.OC-03, GV.OC-05, GV.SC-01, GV.SC-04, GV.SC-06',
    etsi: 'Clause 7.14.1',
    cen: 'ISP-02, DEV-08, PM-01, PM-02, PM-04, PM-05',
    national: {
      BE: 'IMPORTANT: ID.BE-1.1, ID.GV-1.2, ID.SC-2.1',
      FI: 'THIRD-PARTIES-1, THIRD-PARTIES-2, CRITICAL-1',
      EL: 'Articles 12.1a, 12.1c, 12.1d, 12.1e, 12.2a, 12.2b',
      ES: 'Article 2, Article 15, Article 16, Article 19, Article 23',
      FR: '2.B.5-IE/EE, 3.A.1-IE/EE, 3.A.2-IE/EE',
    },
  },
  {
    id: '5.2',
    title: 'Directory of suppliers and service providers',
    iso27001: 'A.5.22',
    nist: 'GV.OC-05, GV.SC-04, ID.IM-01, ID.IM-02, ID.IM-03',
    etsi: 'REQ-7.14.3-11X, REQ7.14.3-12X, Clause 7.14.3',
    cen: 'DEV-02, PM-03',
    national: {
      BE: 'BASIC: ID.GV-4.1, ID.RA5.1, IMPORTANT: ID.BE-4.1',
      FI: 'THIRD-PARTIES-1, CRITICAL-1',
      EL: 'Article 12.1b',
      ES: 'Article 13, Annex II: [op.ext] External resources',
      FR: '1.1-IE/EE, 3.A.1-IE/EE, 3.A.2-IE/EE',
    },
  },
  {
    id: '6.1',
    title: 'Security in acquisition of ICT services, ICT systems or ICT products',
    iso27001: 'A.5.21, A.5.23',
    nist: 'GV.PO-02, GV.SC-06, ID.RA-09, ID.RA-10',
    etsi: 'REQ-7.7-01, Clause 7.14.2',
    cen: 'OIS-04, AM-03, DEV-02, DEV-07, PM-01',
    national: {
      BE: 'BASIC: ID.GV-4.1, IMPORTANT: ID.RM-1.1, ID.GV-4.2',
      FI: 'THIRD-PARTIES-1, THIRD-PARTIES-2, ARCHITECTURE-4',
      EL: 'Articles 12.1a, 12.1c, 12.1d, 12.1e, 12.2a, 12.2b',
      ES: 'Article 19, Annex II: [op.pl.3] Acquisition of new components',
      FR: '3.A.1-IE/EE, 5.A.1-EE, 5.B.1-EE',
    },
  },
  {
    id: '6.2',
    title: 'Secure development life cycle',
    iso27001: 'A.8.25, A.8.31',
    nist: 'ID.AM-08, PR.PS-06, ID.IM-01, ID.IM-02',
    etsi: 'REQ-7.7-01, REQ-7.7-02, REQ-7.7-03, REQ-7.7-04',
    cen: 'OIS-04, CCM-04, CCM06, DEV-01, DEV-03',
    national: {
      BE: 'IMPORTANT: ID.GV-1.2, PR.IP-2.1, ESSENTIAL: PR.DS-7.1',
      FI: 'ARCHITECTURE-4, THIRD-PARTIES-2',
      EL: 'Articles 15a, 15b, 15c, 15d',
      ES: 'Article 36, Annex II: [mp.sw.1] IT Applications development',
      FR: '2.B.5-IE/EE, 5.B.1-EE',
    },
  },
  {
    id: '6.3',
    title: 'Configuration management',
    iso27001: 'A.8.9',
    nist: 'PR.PS-01, ID.IM-01, ID.IM-02, ID.IM-03',
    etsi: 'REQ-6.3-08X, REQ-7.7-03, REQ-6.3-09X',
    cen: 'OPS-21, PSS-03, PSS-04',
    national: {
      BE: 'BASIC: PR.IP-4.1, IMPORTANT: ID.AM-3.2, PR.IP-1.1',
      FI: 'ASSET-3, ASSET-4, ARCHITECTURE-3, ARCHITECTURE-4',
      EL: 'Articles 14a, 14b, 14c, 14d, 19h',
      ES: 'Article 10, Article 20, Article 21, Article 30',
      FR: '10.B.2-IE/EE, 11.B.7-EE, 17.2-EE',
    },
  },
  {
    id: '6.4',
    title: 'Change management, repairs and maintenance',
    iso27001: '6.3, 8.1, A.7.13, A.8.32',
    nist: 'ID.RA-07, ID.IM-01, ID.IM-02, ID.IM-03',
    etsi: 'REQ-7.7-03, REQ-7.7-04, REQ-7.7-07X',
    cen: 'ISP-03, CCM-01, CCM-02, CCM-03',
    national: {
      BE: 'BASIC: ID.AM-1.1, ID.AM2.1, ID.GV-1.1',
      FI: 'ASSET-4',
      EL: 'Articles 16a, 16b',
      ES: 'Annex II: [op.exp.5] Change Management',
      FR: '2.C.2-IE/EE, 5.A.1-EE',
    },
  },
  {
    id: '6.5',
    title: 'Security testing',
    iso27001: 'A.8.29, A.8.33, A.8.34',
    nist: 'ID.RA-01, ID.IM-01, ID.IM-02, ID.IM-03',
    etsi: 'REQ-7.8-10, REQ-7.8-13, REQ-7.8-14X',
    cen: 'ISP-02, OPS-19, DEV-01, DEV-04, DEV-06',
    national: {
      BE: 'IMPORTANT: ID.BE-5.1, ID.SC-4.1, PR.IP-3.1',
      FI: 'THREAT-1, THIRD-PARTIES-2',
      EL: 'Articles 8a, 8b, 8c, 8d, 18.1a, 18.1b',
      ES: 'Annex II: [op.nub.1] Cloud Service Protection',
      FR: '3.B.2-IE/EE, 13.2-IE/EE, 13.3-IE/EE',
    },
  },
  {
    id: '6.6',
    title: 'Security patch management',
    iso27001: 'A.8.31, A.8.32',
    nist: 'PR.PS-02, DE.CM-09, ID.IM-01, ID.IM-02',
    etsi: 'REQ-7.7-07X',
    cen: 'CCM-03, CCM-04, CCM05, OPS-18',
    national: {
      BE: 'BASIC: PR.MA-1.1, IMPORTANT: PR.MA-1.2',
      FI: 'ASSET-4, THREAT-1',
      EL: 'Articles 17c, 17d',
      ES: 'Article 21, Annex II: [op.exp.4] Security maintenance',
      FR: '5.A.1-EE, 5.B.1-EE, 5.B.2-IE/EE',
    },
  },
  {
    id: '6.7',
    title: 'Network security',
    iso27001: 'A.8.16, A.8.20',
    nist: 'DE.CM-01, PR.IR-01, PR.PS-05, ID.IM-01',
    etsi: 'Clause 7.8',
    cen: 'PS-04, CS-01, CS-02, CS03, CS-06, CS-07',
    national: {
      BE: 'BASIC: ID.RA-1.1, PR.AC2.1, PR.AC-3.1',
      FI: 'ACCESS-1, ACCESS2, ARCHITECTURE1, ARCHITECTURE-2',
      EL: 'Articles 19a, 19b, 19c, 19d, 19f',
      ES: 'Article 9, Article 10, Article 27',
      FR: '3.A.1-IE/EE, 5.B.3-IE/EE, 7.A.2-EE',
    },
  },
  {
    id: '6.8',
    title: 'Network segmentation',
    iso27001: 'A.8.22',
    nist: 'PR.IR-01, ID.IM-01, ID.IM-02, ID.IM-03',
    etsi: 'REQ-7.8-02, REQ-7.8-03, REQ-7.8-04',
    cen: 'IAM-09, CS-02, CS-04, CS05',
    national: {
      BE: 'BASIC: PR.AC-3.1, PR.AC5.2, IMPORTANT: PR.AC-5.3',
      FI: 'ARCHITECTURE-1, ARCHITECTURE-2',
      EL: 'Articles 19e, 19f, 19k',
      ES: 'Annex II: [mp.com.4] Separation of information flows',
      FR: '7.A.1-IE/EE, 7.A.2-EE, 7.A.3-EE',
    },
  },
  {
    id: '6.9',
    title: 'Protection against malicious and unauthorised software',
    iso27001: 'A.5.32, A.8.7',
    nist: 'DE.CM-01, DE.CM-09, PR.PS-05, ID.IM-01',
    etsi: 'REQ-7.7-05, REQ-7.8-15X, REQ-7.8-16X',
    cen: 'OPS-04, OPS-05, CS-03',
    national: {
      BE: 'BASIC: ID.AM-2.1, ID.RA1.1, PR.PT-4.1',
      FI: 'ARCHITECTURE-2, ARCHITECTURE-3',
      EL: 'Articles 20a, 20b, 20c, 20d, 20e',
      ES: 'Article 24, Annex II: [op.exp.6] Protection against harmful code',
      FR: '5.B.1-EE, 5.B.2-IE/EE, 5.B.3-IE/EE',
    },
  },
  {
    id: '6.10',
    title: 'Vulnerability handling and disclosure',
    iso27001: 'A.8.8',
    nist: 'ID.RA-01, ID.RA-02, ID.RA-04, ID.RA-05',
    etsi: 'REQ-7.8-13, REQ-7.8-14X, REQ-7.9.2-09X',
    cen: 'OIS-03, OPS-17, OPS-18, OPS-19',
    national: {
      BE: 'BASIC: ID.RA-1.1, IMPORTANT: ID.RA-1.2',
      FI: 'THREAT-1',
      EL: 'Articles 5.2, 17a, 17b, 17e, 17f, 17g',
      ES: 'Article 8, Article 10, Article 21, Article 34',
      FR: '5.B.1-EE, 5.B.2-IE/EE, 5.B.3-IE/EE',
    },
  },
  {
    id: '7.1',
    title: 'Policies and procedures to assess effectiveness of cybersecurity risk-management measures',
    iso27001: '6.2, 9.1, 9.3',
    nist: 'ID.IM-03, GV.RM-06, ID.IM-01, ID.IM-02',
    etsi: 'Clause 5, ref. to ISO/IEC 27005:2022',
    cen: 'ISP-02, OPS-20, CO-04',
    national: {
      BE: 'BASIC: RS.IM-1.1, IMPORTANT: PR.IP-7.1',
      FI: 'CRITICAL-2, RISK-4, RISK-5, Management activities',
      EL: 'Articles 18.1a, 18.1b, 18.1c, 18.1d',
      ES: 'Article 7, Article 10, Article 27, Article 31',
      FR: '2.B.2-IE/EE, 2.B.5-IE/EE, 15.3-EE',
    },
  },
  {
    id: '8.1',
    title: 'Awareness raising and basic cyber hygiene practices',
    iso27001: '7.3, A.6.3, A.8.7',
    nist: 'PR.AT-01, PR.AT-02, ID.IM-01, ID.IM-02',
    etsi: 'REQ-7.2-01X, REQ-7.2-02, REQ-7.2-03X',
    cen: 'HR-04, DOC-01',
    national: {
      BE: 'BASIC: PR.AT-1.1, IMPORTANT: PR.AT-1.2',
      FI: 'WORKFORCE-2, WORKFORCE-3, WORKFORCE-4',
      EL: 'Articles 4b.e, 21a, 21c',
      ES: 'Article 6, Annex II [mp.per.3] Awareness',
      FR: '4.1-IE/EE, 4.2-IE/EE, 4.3-EE',
    },
  },
  {
    id: '8.2',
    title: 'Security training',
    iso27001: '7.2, A.6.3',
    nist: 'PR.AT-01, ID.IM-01, ID.IM-02, ID.IM-03',
    etsi: 'REQ-7.2-04X, REQ-7.2-02, REQ-7.2-05X',
    cen: 'HR-04, PM-01',
    national: {
      BE: 'BASIC: PR.AT-1.1, IMPORTANT: PR.AT-1.2, PR.AT-5.1',
      FI: 'WORKFORCE-1, WORKFORCE-2, WORKFORCE-3',
      EL: 'Articles 21b, 21c',
      ES: 'Article 15, Article 16, Annex II [mp.per.4] Training',
      FR: '4.2-IE/EE, 4.3-EE, 4.4-IE/EE',
    },
  },
  {
    id: '9.1',
    title: 'Cryptography',
    iso27001: 'A.5.31, A.8.24',
    nist: 'PR.DS-01, PR.DS-02, ID.IM-01, ID.IM-02',
    etsi: 'Clause 7.5, ref. to clause 8.24 of ISO/IEC 27002:2022',
    cen: 'ISP-02, CKM-01, CKM-02, CKM-03',
    national: {
      BE: 'IMPORTANT: PR.DS-6.1, ESSENTIAL: PR.AC-3.4',
      FI: 'ARCHITECTURE-5',
      EL: 'Articles 22a, 22b, 22c, 22d, 22e',
      ES: 'Article 10, Article 27, Annex II: [op.exp.10] Cryptographic',
      FR: '2.B.4-IE/EE, 8.1-IE/EE, 8.2-IE/EE',
    },
  },
  {
    id: '10.1',
    title: 'Human resources security',
    iso27001: '7.1, 7.2, A.6.2, A.6.3',
    nist: 'PR.AT-02, GV.RR-04, ID.IM-01, ID.IM-02',
    etsi: 'Clause 7.2',
    cen: 'HR-01, HR-02, HR-03',
    national: {
      BE: 'BASIC: ID.GV-1.1, PR.AC1.1, PR.AC-4.3',
      FI: 'WORKFORCE-1, WORKFORCE-2, WORKFORCE-3',
      EL: 'Articles 4b.c, 7c',
      ES: 'Article 15, Annex II: [mp.per.1] Job characterization',
      FR: '1.1-IE/EE, 2.A.1-IE/EE, 2.A.2-EE',
    },
  },
  {
    id: '10.2',
    title: 'Verification of Background',
    iso27001: 'A.6.1',
    nist: 'GV.RR-04, ID.IM-01, ID.IM-02, ID.IM-03',
    etsi: 'REQ-7.2-09X, REQ-7.2-17',
    cen: 'HR-02',
    national: {
      BE: 'BASIC: PR.IP-11.1, IMPORTANT: PR.IP-11.2',
      FI: 'WORKFORCE-1',
      EL: 'Articles 10.1a, 10.2',
      ES: 'Article 15, Article 16, Annex II: [mp.per.1] Job characterization',
      FR: '4.4-IE/EE',
    },
  },
  {
    id: '10.3',
    title: 'Termination or change of employment procedures',
    iso27001: 'A.6.5',
    nist: 'GV.RR-04',
    etsi: 'REQ-7.3.2-07X, REQ-7.4-08X',
    cen: 'HR-05, HR-06',
    national: {
      BE: 'BASIC: ID.GV-3.1, PR.AC4.3, PR.IP-11.1',
      FI: 'WORKFORCE-1, ACCESS-1, ACCESS2, ACCESS-3',
      EL: 'Article 10.1b',
      ES: 'Annex II: [org.2] Security regulations',
      FR: '2.B.2-IE/EE, 4.3-EE, 4.4-IE/EE',
    },
  },
  {
    id: '10.4',
    title: 'Disciplinary process',
    iso27001: '5.28, A.6.4',
    nist: 'ID.IM-01, ID.IM-02, ID.IM-03',
    etsi: 'REQ-7.2-06X',
    cen: 'HR-01',
    national: {
      BE: 'BASIC: ID.GV-3.1, IMPORTANT: ID.GV-3.2',
      FI: 'WORKFORCE-1',
      EL: 'Article 10.1c',
      ES: 'Annex II: [org.2] Security regulations',
      FR: '2.C.2-IE/EE, 3.B.1-IE/EE, 3.B.2-IE/EE',
    },
  },
  {
    id: '11.1',
    title: 'Access control policy',
    iso27001: 'Α.5.15, A.7.2, Α.8.3, Α.8.21, A9',
    nist: 'PR.AA-05, ID.IM-01, ID.IM-02, ID.IM-03',
    etsi: 'REQ-7.4-02X, REQ-7.4-09X, REQ-7.4-13X',
    cen: 'OIS-02, ISP-02, IAM-01',
    national: {
      BE: 'BASIC: ID.AM-5.1, ID.GV1.1, PR.AC-4.1',
      FI: 'ACCESS-1, ACCESS2, ACCESS-3, ACCESS-4',
      EL: 'Articles 13a, 13h',
      ES: 'Article 12, Article 17, Article 18, Article 20',
      FR: '2.B.5-IE/EE, 2.C.1-IE/EE, 2.C.2-IE/EE',
    },
  },
  {
    id: '11.2',
    title: 'Management of access rights',
    iso27001: 'A.5.3, Α.5.18, A.9',
    nist: 'PR.AA-05, ID.IM-01, ID.IM-02, ID.IM-03',
    etsi: 'Clause 7.4, REQ-7.9.1-04X',
    cen: 'OIS-02, IAM-04, IAM-05, PSS-03',
    national: {
      BE: 'BASIC: ID.AM-5.1, ID.GV1.1, PR.AC-1.1',
      FI: 'ACCESS-1, ACCESS2, ACCESS-3, ACCESS-4',
      EL: 'Articles 13b, 13c, 13e, 13h',
      ES: 'Article 20, Annex II: [op.acc.1] Identification',
      FR: '6.1-IE/EE, 6.2-EE, 6.3-EE',
    },
  },
  {
    id: '11.3',
    title: 'Privileged accounts and system administration accounts',
    iso27001: 'Α.8.2, A.8.18, A.9',
    nist: 'ID.IM-01, ID.IM-02, ID.IM-03',
    etsi: 'REQ-7.4-10X',
    cen: 'ISP-02, IAM-05, IAM-06',
    national: {
      BE: 'BASIC: PR.AC-1.1, PR.AC4.3, IMPORTANT: PR.AC-4.7',
      FI: 'ACCESS-1, ACCESS2, ACCESS-3, ACCESS-4',
      EL: 'Articles 13b, 13d, 13h',
      ES: 'Article 20, Annex II: [op.acc.1] Identification',
      FR: '4.5-IE/EE, 11.A.1-IE/EE, 11.A.2-IE/EE',
    },
  },
  {
    id: '11.4',
    title: 'Administration systems',
    iso27001: 'Α.8.2, A.8.18',
    nist: '-',
    etsi: 'REQ-7.4-10X, REQ-7.4-02X, REQ-7.4-03X',
    cen: 'OIS-02, IAM-06, IAM-09',
    national: {
      BE: 'BASIC: PR.AC-4.4, IMPORTANT: PR.AC-5.4',
      FI: 'ACCESS-1, ACCESS2, ACCESS-3, ACCESS-4',
      EL: 'Articles 13a, 13d',
      ES: 'Article 20, Annex II: [op.acc.1] Identification',
      FR: '7.A.1-IE/EE, 7.A.4-EE, 7.B.1-IE/EE',
    },
  },
  {
    id: '11.5',
    title: 'Identification',
    iso27001: 'Α.5.16',
    nist: 'PR.AA-01, PR.AA-05, PR.AC-02',
    etsi: 'REQ-7.4-11X, REQ-7.4-08X, REQ-7.4-12X',
    cen: 'IAM-02, IAM-03, IAM-06',
    national: {
      BE: 'BASIC: PR.AC-1.1, PR.AC4.1, IMPORTANT: PR.AC-1.2',
      FI: 'ACCESS-1',
      EL: 'Articles not specified',
      ES: 'Article 20, Annex II: [op.acc.1] Identification',
      FR: '4.4-IE/EE, 10.A.1-IE/EE, 10.A.2-IE/EE',
    },
  },
  {
    id: '11.6',
    title: 'Authentication',
    iso27001: 'Α.5.17',
    nist: 'PR.AA-05, PR.AA-03, ID.IM-01, ID.IM-02',
    etsi: 'REQ-7.4-02X, REQ-7.4-05X, REQ-7.7-06X',
    cen: 'IAM-07, IAM-08',
    national: {
      BE: 'BASIC: PR.AC-3.2, IMPORTANT: PR.AC-1.2',
      FI: 'ACCESS-1, ACCESS2, ACCESS-3, ACCESS-4',
      EL: 'Articles 13a, 13f, 13g, 13h',
      ES: 'Article 20, Annex II: [op.acc.1] Identification',
      FR: '4.4-IE/EE, 8.1-IE/EE, 8.2-IE/EE',
    },
  },
  {
    id: '11.7',
    title: 'Multi-factor authentication',
    iso27001: 'Α.8.5',
    nist: 'PR.AA-03',
    etsi: 'REQ-7.4-05X, REQ-7.4-06X',
    cen: 'OPS-23, IAM-06, IAM-07',
    national: {
      BE: 'BASIC: PR.AC-3.2, IMPORTANT: PR.AC-1.2',
      FI: 'ACCESS1',
      EL: 'Article 13g',
      ES: 'Article 20, Annex II: [op.acc.1] Identification',
      FR: '8.1-IE/EE, 8.2-IE/EE, 8.3-EE',
    },
  },
  {
    id: '12.1',
    title: 'Asset classification',
    iso27001: 'A.5.9, A.5.12, A.5.13',
    nist: 'ID.AM-05',
    etsi: 'REQE-7.3.2-01X, REQ-7.3.2-02X, REQ-7.3.2-03X',
    cen: 'AM-05',
    national: {
      BE: 'BASIC: ID.AM-5.1',
      FI: 'CRITICAL-1, CRITICAL-2, ASSET1, ASSET-2',
      EL: 'Article 11b',
      ES: 'Article 40, Annex II: [op.pl.1] Risk analysis',
      FR: '5.A.1-EE',
    },
  },
  {
    id: '12.2',
    title: 'Handling of assets',
    iso27001: 'A.5.9, A.5.10, A.5.14, A.7.10',
    nist: 'ID.IM-01',
    etsi: 'REQ-7.3.2-06X, REQ-7.3.3-01X, REQ-7.3.3-02X',
    cen: 'ISP-02, AM-02, AM-03',
    national: {
      BE: 'BASIC: ID.AM-1.1, ID.AM3.1, ID.GV-4.1',
      FI: 'PROGRAM-2, ASSET-1, ASSET-2, ASSET-5',
      EL: 'Article 11c',
      ES: 'Article 40, Annex II: [op.pl.1] Risk analysis',
      FR: '1.1-IE/EE, 2.A.3-IE/EE, 3.A.1-IE/EE',
    },
  },
  {
    id: '12.3',
    title: 'Removable media policy',
    iso27001: 'A.7.7, A.7.10',
    nist: 'PR.DS-01, ID.IM-01, ID.IM-02, ID.IM-03',
    etsi: 'REQ-7.3.3-01X, REQ-7.3.3-02X, REQ-7.3.3-03X',
    cen: 'ISP-02, AM-02, PS-04',
    national: {
      BE: 'BASIC: PR.DS-3.1, IMPORTANT: ID.GV-1.2',
      FI: 'ARCHITECTURE-3g, ARCHITECTURE-5g, ARCHITECTURE-6c',
      EL: 'Article 11d',
      ES: 'Annex II: [mp.eq.3] Protection of portable devices',
      FR: '2.8.1-IE/EE, 9.5.IE/EE, 9.6-IE/EE',
    },
  },
  {
    id: '12.4',
    title: 'Asset inventory',
    iso27001: 'A.5.9',
    nist: 'ID.AM-01, ID.AM-02, ID.AM-03, ID.AM-04',
    etsi: 'Clause 7.3',
    cen: 'AM-04',
    national: {
      BE: 'BASIC: ID.AM-1.1, ID.AM2.1, IMPORTANT: ID.AM-1.2',
      FI: 'ASSET-1, ASSET-4',
      EL: 'Article 11a',
      ES: 'Annex II: [op.exp.1] Asset inventory',
      FR: '5.A.1-EE',
    },
  },
  {
    id: '12.5',
    title: 'Deposit, return or deletion of assets upon termination of employment',
    iso27001: 'A.5.11, A.5.18, A.8.24',
    nist: '-',
    etsi: 'REQ-7.3.2-07X, REQ-7.3.3-01X, REQ-7.3.3-02X',
    cen: 'AM-01',
    national: {
      BE: 'BASIC: PR.IP-11.1, IMPORTANT: PR.AT-3.2',
      FI: 'WORKFORCE-1',
      EL: 'Article 11c',
      ES: 'Annex II: [mp.si.5] Erasure and destruction',
      FR: '4.1-IE/EE, 4.2-IE/EE, 4.3-EE',
    },
  },
  {
    id: '13.1',
    title: 'Supporting utilities',
    iso27001: 'A.7.11',
    nist: 'DE.CM-02, DE.CM-06, GV.OC-03, GV.OC-05',
    etsi: 'REQ-7.6-03, REQ-7.6-05, REQ-7.11.2-01X',
    cen: 'PS-05',
    national: {
      BE: 'BASIC: ID.GV-3.1, RS.IM1.1, IMPORTANT: ID.BE-1.1',
      FI: 'RESPONSE-4, CRITICAL-3',
      EL: 'Articles 23a, 23e, 23f',
      ES: 'Article 2, Annex II: [mp.if.1] Separate areas',
      FR: '2.B.2-IE/EE, 3.A.1-IE/EE, 3.A.2-IE/EE',
    },
  },
  {
    id: '13.2',
    title: 'Protection against physical and environmental threats',
    iso27001: 'A.7.3, A.7.5',
    nist: 'PR.IR-02, ID.IM-01, ID.IM-02, ID.IM-03',
    etsi: 'Clause 7.6',
    cen: 'PS-05',
    national: {
      BE: 'BASIC: RS.IM-1.1, IMPORTANT: PR.IP-5.1',
      FI: 'RISK-1, RISK-2, RISK3, RISK-4, THREAT2',
      EL: 'Articles 23a, 23d, 23f',
      ES: 'Article 2, Annex II: [mp.if.1] Separate areas',
      FR: '12.5-EE, 14.4-EE, 16.1-EE',
    },
  },
  {
    id: '13.3',
    title: 'Perimeter and physical access control',
    iso27001: 'A.7.1, A.7.2, A.7.4',
    nist: 'PR.AA-06, DE.CM-02',
    etsi: 'Clause 7.6',
    cen: 'PS-01, PS-02, PS-03, PS04',
    national: {
      BE: 'BASIC: ID.GV-1.1, PR.AC2.1, PR.AC-3.1',
      FI: 'RISK-1, RISK-2, RISK3, ARCHITECTURE3',
      EL: 'Articles 23a, 23b, 23c, 23f',
      ES: 'Article 18, Annex II: [mp.if.1] Separate areas',
      FR: '2.B.2-IE/EE, 6.1-IE/EE, 6.2-EE',
    },
  },
];
